/**
 * Global handling for an expired session.
 *
 * Before this, a session that lapsed mid-use produced whatever each panel did with
 * a rejected query — in practice the raw thrown message, `API error 401 (/api/…):
 * …`, rendered as body text in one card while the rest of the page looked fine.
 * Exactly one call site branched on 401 (`repository-lifecycle-ui.ts`). The operator
 * had to infer "I have been signed out" from a string meant for a log.
 *
 * The decisions live here as pure functions so they are testable without a query
 * client, a router, or a network: `lib/query-client.ts` supplies the IO.
 */

/** The query key AuthGate, RootRoute and TopNav share for the session probe. */
export const SESSION_QUERY_KEY = 'authSession';

/** HTTP status carried on errors thrown by `fetchJSON` in `lib/api.ts`. */
function statusOf(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const { status } = error as { status?: unknown };
  return typeof status === 'number' ? status : undefined;
}

/** True for a 401 from any API call. 403 is NOT included: it means "signed in, not
 *  allowed", which must not sign the operator out. */
export function isUnauthorized(error: unknown): boolean {
  return statusOf(error) === 401;
}

/**
 * A 4xx will not fix itself, so retrying one only delays the outcome — up to three
 * backed-off attempts before a 401 reached its handler, and the same before a 404
 * reached the panel that should render "not found".
 *
 * 408 and 429 are the exceptions: both explicitly mean "try again".
 */
export function isRetriableFailure(error: unknown): boolean {
  const status = statusOf(error);
  if (status === undefined) return true; // network/parse failure — worth another try
  if (status === 408 || status === 429) return true;
  return status < 400 || status >= 500;
}

/**
 * Whether a failed query should trigger session revalidation.
 *
 * The session probe itself is excluded, and that exclusion is the point rather than
 * a detail: revalidating is *itself* a refetch of that key, so a 401 on the session
 * query would schedule the request that produced it, without bound. `/api/auth/session`
 * answers 200 with `authenticated: false` when there is no session, so it should
 * never 401 — "should never" is not a reason to build a loop that fires when it does.
 */
export function shouldRevalidateSession(input: {
  readonly error: unknown;
  readonly queryKey: readonly unknown[];
}): boolean {
  if (!isUnauthorized(input.error)) return false;
  return input.queryKey[0] !== SESSION_QUERY_KEY;
}

/**
 * Whether the operator reached the sign-in screen by expiry rather than by never
 * having signed in — the difference between "Sign in to continue" and an
 * explanation of why the app just disappeared.
 *
 * Module-level rather than React state, deliberately: the writer is a query-cache
 * callback that runs outside the component tree and has no access to it. It is safe
 * because the read is ORDERED, not raced — the same invalidation that sets this flag
 * is what re-renders AuthGate and mounts LoginScreen, so the write always precedes
 * the read. Nothing subscribes to it and nothing should; it is a one-shot note
 * consumed by the next render.
 */
let expiredDuringSession = false;

export function markSessionExpired(): void {
  expiredDuringSession = true;
}

export function wasSessionExpired(): boolean {
  return expiredDuringSession;
}

/** Called on a successful sign-in, so a later visit does not inherit a stale notice. */
export function clearSessionExpired(): void {
  expiredDuringSession = false;
}
